import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PackageOpen, TruckIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RequestReturnCardProps {
  boxId: string;
  boxName: string;
  onReturnRequested: () => void;
}

export function RequestReturnCard({ boxId, boxName, onReturnRequested }: RequestReturnCardProps) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [notes, setNotes] = useState("");

  const handleRequestReturn = async () => {
    try {
      setIsRequesting(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("You must be logged in to request a return");
        return;
      }

      const { error } = await supabase.functions.invoke("request-box-return", {
        body: { 
          boxId,
          notes: notes.trim() || undefined
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      toast.success("Return request submitted successfully!");
      onReturnRequested();
    } catch (error: any) {
      console.error("Error requesting return:", error);
      toast.error(error.message || "Failed to request return");
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <PackageOpen className="h-5 w-5" />
          Request Box Return
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Need this box back? We'll ship it to your address on file.
          </p>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Additional Notes (Optional)</label>
            <Textarea
              placeholder="Any special instructions or notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <Button
          onClick={handleRequestReturn}
          disabled={isRequesting}
          className="w-full"
        >
          <TruckIcon className="h-4 w-4 mr-2" />
          {isRequesting ? "Submitting Request..." : "Request Return Delivery"}
        </Button>

        <div className="pt-3 border-t">
          <p className="text-xs text-muted-foreground">
            Our team will process your request and send you shipping details within 1-2 business days.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
